/**
 * Qual build está rodando NESTE aparelho. Fonte única do cliente.
 *
 * Existe porque relato de teste sem versão é relato sem valor: "o ímã soltou
 * sozinho" só vira dado quando se sabe em qual build soltou. Até aqui o `GIT_SHA`
 * chegava ao servidor e ao `/health`, mas NEVER ao bundle — então o aparelho era o
 * único participante do teste que não sabia dizer o que estava executando.
 *
 * O par `sha` + `time` responde as duas perguntas que um teste faz, e são
 * DIFERENTES: `sha` diz *qual código*, e `time` diz *se o que estou vendo é o que
 * acabou de subir* — é o `time` que denuncia página velha em cache, que é a forma
 * mais comum de testar a versão errada sem perceber.
 *
 * Env injetável no parâmetro, igual a `flags.ts`: o teste NEVER precisa de browser.
 */

export interface BuildInfo {
  /** SHA curto do commit, ou `dev` fora de deploy. Sufixo `-dirty` é significativo. */
  readonly sha: string;
  /** Instante do build, ISO em UTC. `''` quando o build NEVER carimbou. */
  readonly time: string;
  /** `stg`, `prod` ou `dev`. Rótulo de ambiente, NEVER usado para lógica. */
  readonly env: string;
  /**
   * O build saiu de árvore suja. É GRAVE em teste: o que está no ar NEVER é
   * reproduzível a partir do git, então o replay e o commit NEVER se encontram.
   */
  readonly dirty: boolean;
}

const UNKNOWN_SHA = 'dev';
const DIRTY_SUFFIX = '-dirty';

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Lê o carimbo que o `vite.config.ts` injetou. Sem carimbo devolve `dev` —
 * NEVER string vazia, porque um SHA vazio na tela lê como "não tem versão" em vez
 * de "estou rodando local", e são coisas diferentes.
 */
export function buildInfo(env: Record<string, unknown> = import.meta.env): BuildInfo {
  const rawSha = str(env['VITE_BUILD_SHA']);
  const sha = rawSha === '' ? UNKNOWN_SHA : rawSha;
  return {
    sha,
    time: str(env['VITE_BUILD_TIME']),
    env: str(env['VITE_BUILD_ENV']) || UNKNOWN_SHA,
    dirty: sha.endsWith(DIRTY_SUFFIX),
  };
}

/** Os 7 primeiros do SHA, sem o `-dirty` — o sufixo é mostrado como símbolo. */
export function shortSha(sha: string): string {
  const clean = sha.endsWith(DIRTY_SUFFIX) ? sha.slice(0, -DIRTY_SUFFIX.length) : sha;
  return clean.slice(0, 7);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * `15/08 14:32`, no fuso DO APARELHO de propósito: quem lê o carimbo acabou de
 * receber um link e quer comparar com o relógio na mão dele. UTC obrigaria a
 * conversão mental exatamente no momento em que a pergunta é "isto é o que subiu
 * agora?", e é aí que o erro custa uma sessão de teste inteira.
 */
export function formatBuildTime(iso: string): string {
  if (iso === '') return '?';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '?';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * A linha que aparece no canto: `stg · a1b2c3d · 15/08 14:32`.
 * `⚠` na frente quando sujo — é o único estado em que o carimbo pede ação.
 */
export function buildStamp(info: BuildInfo): string {
  const parts = [info.env, shortSha(info.sha), formatBuildTime(info.time)];
  return `${info.dirty ? '⚠ ' : ''}${parts.join(' · ')}`;
}

/**
 * O que vai para a área de transferência ao tocar no carimbo, e o que acompanha
 * todo evento de telemetria. Completo de propósito: o carimbo da tela é curto para
 * caber no canto, e o curto NEVER serve para colar num relato.
 */
export function buildLine(info: BuildInfo): string {
  return `${info.env} ${info.sha} ${info.time === '' ? 'sem-carimbo' : info.time}`;
}
